// The sidebar's current item (Console D): one key for one page, so exactly one row is current
// (the old nav matched path endings and marked two on /performance/start). A key under
// "settings." turns the sidebar into the settings navigation.
import { SETTINGS_SECTIONS, type SettingsSection } from '../routes/search'

export type NavKey =
  | 'home'
  | 'agents'
  | 'instrumentation'
  | 'signals'
  | 'alerts'
  | 'issues'
  | 'releases'
  | 'health'
  | 'perf'
  | 'perf.start'
  | 'perf.screens'
  | 'perf.network'
  | 'perf.hangs'
  | 'perf.frames'
  | 'perf.resources'
  | 'sessions'
  | 'users'
  | 'journeys'
  | 'explore'
  | 'logs'
  | 'dashboards'
  | 'setup'
  | `settings.${SettingsSection}`

const EXACT: Record<string, NavKey> = {
  '/home': 'home',
  '/agents': 'agents',
  '/instrumentation': 'instrumentation',
  '/signals': 'signals',
  '/releases': 'releases',
  '/health': 'health',
  '/performance': 'perf',
  '/journeys': 'journeys',
  '/explore': 'explore',
  '/logs': 'logs',
  '/dashboards': 'dashboards',
  '/setup': 'setup',
}

const PREFIX: readonly [RegExp, NavKey][] = [
  [/^\/releases\//, 'releases'],
  // App exits opens from Health (APS-417).
  [/^\/health\//, 'health'],
  // A rule's page is Signals' (D114).
  [/^\/signals\//, 'signals'],
  [/^\/issues(\/|$)/, 'issues'],
  [/^\/alerts(\/|$)/, 'alerts'],
  // App flags live under Issues (Console D).
  [/^\/flags(\/|$)/, 'issues'],
  [/^\/sessions(\/|$)/, 'sessions'],
  // People and one person; the sidebar marks a pinned person's own row instead (Sidebar.tsx).
  [/^\/users(\/|$)/, 'users'],
  [/^\/performance\/start(\/|$)/, 'perf.start'],
  [/^\/performance\/screens(\/|$)/, 'perf.screens'],
  [/^\/performance\/network(\/|$)/, 'perf.network'],
  [/^\/performance\/hangs(\/|$)/, 'perf.hangs'],
  [/^\/performance\/frames(\/|$)/, 'perf.frames'],
  [/^\/performance\/resources(\/|$)/, 'perf.resources'],
  // A trace opens from an endpoint's samples.
  [/^\/traces\//, 'perf.network'],
]

/** The person a path inside a project opens (`/users/u_4f2a`), decoded; the sidebar's pin row. */
export function personHash(path: string): string | undefined {
  const m = /^\/users\/([^/?#]+)\/?$/.exec(path)
  if (!m?.[1]) return undefined
  try {
    return decodeURIComponent(m[1])
  } catch {
    return undefined
  }
}

/** The current item for a path inside a project (`/issues/abc`), if the sidebar has one. */
export function navKey(path: string): NavKey | undefined {
  const clean = path.replace(/\/+$/, '') || '/'
  const exact = EXACT[clean]
  if (exact) return exact
  const settings = /^\/settings\/([a-z]+)$/.exec(clean)?.[1]
  if (settings && (SETTINGS_SECTIONS as readonly string[]).includes(settings))
    return `settings.${settings as SettingsSection}`
  return PREFIX.find(([re]) => re.test(clean))?.[1]
}

export const isSettingsKey = (key: NavKey | undefined): key is `settings.${SettingsSection}` =>
  !!key?.startsWith('settings.')
